/**
 *
 * @description 模板编辑首页的菜单
 *
 * */

import { useEffect, forwardRef, useImperativeHandle } from 'react';
import { Tree, Dropdown, Input, Menu, Modal, Spin } from 'antd';
import SvgIcon from '&/components/SvgIcon';
import { getTargetElement } from '&/utils/common';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { createUUID } from '&/utils/common';
import { useEventEmitter } from '&/utils/customHooks/useEventEmitter';
import { deepCopy } from '&/utils/common';
import { getTreeTargetData } from '@/utils/common';
import './index.scss';

const LeftTreeMenu = forwardRef((props, ref) => {
    const {
        treeMenuData,
        menuOpt = {},
        initComponent,
        hasInterceptBeforeChangeNode,
        getResetTreeKey,
        updateType,
        defaultExpandedKeys,
        autoChangeSelectedKeys,
        changeAutoChangeSelectedKeys,
    } = props;
    const {
        menuTitle,
        showTitleAdd,
        titleAdd,
        nodeMenu,
        selectNode = () => {},
        customTitleMenu,
        saveNode,
        updateNode,
        removeNode,
        beforeChangeNode,
        beforeAddNode,
        afterAddNode,
        inputMaxLength,
        verifyValue,
        verifySaveValue,
        updateTreeData,
    } = menuOpt;

    const treeRef = useRef();
    const wrap = useRef();
    const selectFlag = useRef(true);
    const currentNode = useRef();
    const newNodeKey = useRef();
    const handler = useRef();

    const [selectedData, setSelectedData] = useState({});
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [treeData, setTreeData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const { emit } = useEventEmitter();

    useImperativeHandle(ref, () => {
        return {
            onAdd,
            handleMore,
            titleAddHandle,
            setTreeData,
            onSelect,
        };
    });

    const [newNode, setNewNode] = useState(null);

    useEffect(() => {
        if (
            treeMenuData.length &&
            (!selectedKeys.length || autoChangeSelectedKeys)
        ) {
            treeDataChange();
        }
        updateTreeData?.(treeData, newNode);
        if (newNode) goScrollToView(newNode.key);
    }, [treeData]);

    useEffect(() => {
        if (treeMenuData.length && selectedKeys.length) {
            resetSelectData();
        }
    }, [treeMenuData]);

    const formatTreeData = obj => {
        const treeObj = deepCopy(obj);
        const formatData = (data, parentKey) => {
            for (var i in data) {
                data[i].isEditable = false;
                data[i].value = data[i].name;
                data[i].defaultValue = data[i].name;
                data[i].title = data[i].value;
                if (parentKey) {
                    data[i].parentKey = parentKey;
                }
                if (data[i].children) {
                    formatData(data[i].children, data[i].key);
                }
            }
        };
        formatData(treeObj);
        return treeObj;
    };

    const initTreeData = () => {
        if (treeMenuData.length > 0) {
            setTreeData(formatTreeData(treeMenuData));
        } else {
            setTreeData([]);
        }
    };

    useEffect(() => {
        initTreeData();
    }, [treeMenuData, initComponent]);

    const treeDataChange = () => {
        let key = [treeMenuData[0].key];
        if (treeMenuData[0]?.children?.length > 0) {
            const expandedKeys = defaultExpandedKeys
                ? [defaultExpandedKeys]
                : [treeMenuData[0].key];
            setExpandedKeys(expandedKeys);
            key = [treeMenuData[0].key, treeMenuData[0]?.children[0].key];
        }
        const data = { node: treeMenuData[0] };
        if (!autoChangeSelectedKeys) {
            setSelectedKeys(key);
            setSelectedData(treeMenuData[0]);
            // emit('selected-node', { ...data.node });
            selectNode(key, data, treeData, selectedKeys);
        }
        changeAutoChangeSelectedKeys?.(false);
    };

    const goScrollToView = key => {
        treeRef.current.scrollTo({ key });
    };

    const resetSelectData = () => {
        const treeList = formatTreeData(treeMenuData);
        const targetData = getTreeTargetData(treeList, selectedKeys[0], 'key');
        if (targetData && (updateType === 'edit' || !updateType)) {
            selectNode(
                selectedKeys,
                { node: targetData },
                treeData,
                selectedKeys
            );
        } else {
            let key = [treeList[0].key];
            if (getResetTreeKey) {
                key = getResetTreeKey?.(treeMenuData, updateType, selectedData);
            }
            const data = getTreeTargetData(treeList, key[0], 'key');
            setSelectedKeys(key);
            selectNode(key, { node: data }, treeData, key);
        }
    };

    // 缓存最近一次选择的节点
    const prevSelectParams = useRef([]);

    // noCache 不缓存本次的选择
    const onSelect = (keys, data, noCache = false) => {
        if (!data) return;
        const { length } = keys || {};
        !noCache && length && (prevSelectParams.current = [keys, data]);

        const { node } = data;
        let key = [node.key];

        if (node.children?.length) {
            const list = deepCopy(expandedKeys);
            if (list.every(item => item !== node.key)) {
                list.push(node.key);
                setExpandedKeys(list);
            }
            key = [node.key, node.children[0].key];
        } else if (node.parentKey) {
            key = [node.key, node.parentKey];
        }

        if (node.key !== selectedKeys[0]) {
            setSelectedKeys(key);
            setSelectedData(node);
            emit('selected-node', { ...node });
        } else {
            // 不允许反选
            length && setSelectedKeys(keys);
        }
        selectNode(keys, data, treeData, selectedKeys);
    };

    const onExpand = expandedKeys => {
        setExpandedKeys(expandedKeys);
    };

    const onAdd = (nodeData, isLeaf) => {
        let hierarchy = [...treeData];

        if (beforeAddNode?.({ treeData })) {
            // 如果是在模板名称编辑态 重置元素外点击取消事件
            if (inpEl.current) {
                inpEl.current.focus();
                clickOutside(
                    wrap.current,
                    currentNode.current.key,
                    currentNode.current.defaultValue
                );
            }
            return;
        }

        if (newNodeKey.current) {
            //新增node 之前如果已经有别的新增节点，则需要先删除掉
            deleteNode(newNodeKey.current, hierarchy);
        }
        newNodeKey.current = createUUID();
        let node = {
            value: '',
            defaultValue: '',
            key: newNodeKey.current, // 这个 key 应该是唯一的。 Tip: The key should be unique
            parentKey: nodeData?.key || 0,
            isEditable: false,
            isLeaf,
            is_new: 1,
        };

        if (nodeData) {
            hierarchy.forEach(ele => {
                if (nodeData.key === ele.key) {
                    setExpandedKeys(prev => [
                        ...new Set([...prev, nodeData.key]),
                    ]);
                    ele.children.push({ ...node, parentId: ele.id });
                }
            });
        } else {
            !isLeaf && (node.children = []);
            hierarchy.push(node);
        }

        onSelect([node.key], { node }, true);
        setTreeData(hierarchy);
        setNewNode(node);
        afterAddNode?.(nodeData, isLeaf, node, hierarchy);
    };

    const changeNode = (key, value, data) =>
        data.forEach(item => {
            if (item.key === key) {
                item.value = value;
            }
            if (item.children) {
                changeNode(key, value, item.children);
            }
        });

    const onInputChange = (e, key) => {
        e.stopPropagation();
        changeNode(key, e.target.value, treeData);
        setTreeData([...treeData]);
    };

    const removeOutClick = () => {
        handler.current &&
            document.removeEventListener('click', handler.current);
        handler.current = null;
    };

    const closeNode = (key, defaultValue, data) =>
        data.forEach(item => {
            item.isEditable = false;
            if (+item.key === +key) {
                item.value = defaultValue;
            }
            if (item.children) {
                closeNode(key, defaultValue, item.children);
            }
        });

    const onCloseEditor = (key, defaultValue, is_new) => {
        removeOutClick();
        selectFlag.current = true;
        if (is_new === 1) {
            setTreeData(prev => {
                const NEXT = [...prev];
                deleteNode(key, NEXT);
                return NEXT;
            });
            newNodeKey.current = undefined;
            onSelect(...prevSelectParams.current);
        } else {
            setTreeData(prev => {
                const NEXT = [...prev];
                closeNode(key, defaultValue, NEXT);
                return NEXT;
            });
        }
    };

    const onSave = async nodeData => {
        const { key, defaultValue, is_new } = nodeData;
        if (verifyValue) {
            if (!verifyValue?.(nodeData.value)) return;
        }
        if (verifySaveValue) {
            if (!verifySaveValue?.(nodeData, treeData)) return;
        }
        removeOutClick();
        selectFlag.current = true;
        setLoading(true);
        try {
            if (is_new) {
                saveNode && (await saveNode(nodeData, treeData));
            } else {
                updateNode && (await updateNode(nodeData));
            }
            onCloseEditor(key, defaultValue);
            setTimeout(() => {
                onSelect([key], { node: nodeData });
            });
        } catch (e) {
            onCloseEditor(key, defaultValue);
        } finally {
            setLoading(false);
        }
    };

    const clickOutside = (target, key, defaultValue) => {
        if (target) {
            handler.current = event => {
                event.stopPropagation();
                const targets = Array.isArray(target) ? target : [target];
                if (
                    targets.some(targetItem => {
                        const targetElement = getTargetElement(targetItem);
                        return (
                            !targetElement ||
                            targetElement?.contains(event.target)
                        );
                    })
                ) {
                    return;
                }
                onCloseEditor(key, defaultValue, currentNode.current.is_new);
            };

            document.addEventListener('click', handler.current);
        }
    };

    const editNode = (key, data) => {
        data.forEach(item => {
            //   return
            item.isEditable = item.key === key;
            //Tip: Must have, when a node is editable, and you click a button to make other node editable, the node which you don't save yet will be not editable, and its value should be defaultValue
            item.value = item.defaultValue; // 当某节点处于编辑状态，并改变数据，点击编辑其他节点时，此节点变成不可编辑状态，value 需要回退到 defaultvalue
            if (item.children) {
                item.children = editNode(key, item.children);
            }
        });
        return data;
    };

    const onEdit = (key, defaultValue, e) => {
        selectFlag.current = true;

        e?.domEvent.stopPropagation();

        setTreeData(prev => {
            let NEXT = [...prev];
            if (newNodeKey.current && e) {
                //新增的时候，直接去编辑别的节点了，那么先删除新增的节点
                deleteNode(newNodeKey.current, NEXT);
            }
            NEXT = editNode(key, NEXT);
            return NEXT;
        });

        setTimeout(() => {
            clickOutside(wrap.current, key, defaultValue);
        }, 300);
    };

    useEffect(() => {
        if (!newNode) return;
        onEdit(newNode.key, newNode.name);
    }, [newNode]);

    const deleteNode = (key, data) => {
        data.forEach((item, index) => {
            if (item.key === key) {
                data.splice(index, 1);
                return;
            } else {
                if (item.children) {
                    deleteNode(key, item.children);
                }
            }
        });
    };

    const onDelete = (nodeData, e) => {
        if (e) e.domEvent.stopPropagation();
        Modal.confirm({
            title: intl.get('base.warning'),
            icon: <ExclamationCircleOutlined />,
            content: '确定要删除吗',
            centered: true,
            okText: intl.get('base.confirm'),
            cancelText: intl.get('base.cancel'),
            wrapClassName: 'delete-tree-node-modal',
            className: 'delete-tree-node-modal',
            async onOk(close) {
                try {
                    if (nodeData.is_new !== 1) {
                        removeNode && (await removeNode(nodeData, treeData));
                    }
                    deleteNode(nodeData.key, treeData);
                    setTreeData([...treeData]);
                    close();
                } catch {
                    close();
                }
            },
        });
    };

    const titleMenu = nodeData => (
        <Menu>
            {!nodeData.isLeaf && (
                <Menu.Item
                    key="0"
                    onClick={() => {
                        onAdd(nodeData, true);
                    }}>
                    {intl.get('templateSettings.addTemplate')}
                </Menu.Item>
            )}
            <Menu.Item
                key="1"
                onClick={e => onEdit(nodeData.key, nodeData.defaultValue, e)}>
                {intl.get('base.rename')}
            </Menu.Item>
            <Menu.Item key="2" onClick={e => onDelete(nodeData, e)}>
                {intl.get('base.delete')}
            </Menu.Item>
        </Menu>
    );

    const handleMore = e => {
        removeOutClick();
        e.stopPropagation();
    };

    const titleAddHandle = e => {
        removeOutClick();
        if (titleAdd) {
            titleAdd();
        } else {
            e?.stopPropagation?.();
            onAdd(null, true);
        }
    };

    const inpEl = useRef();

    const titleRender = nodeData => {
        if (nodeData.isEditable) {
            return (
                <span
                    onClick={e => e.stopPropagation()}
                    ref={function (span) {
                        if (span !== null) {
                            wrap.current = span;
                        }
                    }}
                    className="edit-wrap"
                    style={{ overflow: 'hidden' }}>
                    <Input
                        ref={function (input) {
                            if (input != null) {
                                if (selectFlag.current) {
                                    selectFlag.current = false;
                                    input.select();
                                }
                                input.focus();
                                currentNode.current = nodeData;
                            }
                            inpEl.current = input;
                        }}
                        maxLength={inputMaxLength}
                        size="mini"
                        className="edit-input"
                        value={nodeData.value}
                        onChange={e => onInputChange(e, nodeData.key)}
                        onPressEnter={() => onSave(nodeData)}
                    />
                    <SvgIcon
                        iconName="icon-confirm"
                        onClick={() =>
                            nodeData.value === '' ? '' : onSave(nodeData)
                        }
                        className={`edit-icon ${
                            nodeData.value === '' ? 'disabled-hdc-dom' : ''
                        }`}
                    />
                    <SvgIcon
                        className="edit-icon"
                        iconName="icon-del"
                        onClick={() =>
                            onCloseEditor(
                                nodeData.key,
                                nodeData.defaultValue,
                                nodeData.is_new
                            )
                        }
                    />
                </span>
            );
        }
        return (
            <>
                <div className="left">
                    <div className="text-wrap">
                        <div className="hide-text">{nodeData.value}</div>
                        <div className={`text-no-wrap title`}>
                            {nodeData.value}
                        </div>
                    </div>
                </div>
                {nodeMenu ? (
                    nodeMenu(nodeData)
                ) : (
                    <Dropdown
                        overlay={
                            customTitleMenu
                                ? customTitleMenu()
                                : titleMenu(nodeData)
                        }
                        trigger={['click']}
                        className="directory-dropdown">
                        <i
                            className="icon-more-h filled node__more-menu"
                            onClick={handleMore}></i>
                    </Dropdown>
                )}
            </>
        );
    };

    const onTreeClick = async (keys, data) => {
        // 如果在表单页面
        if (
            hasInterceptBeforeChangeNode &&
            (await beforeChangeNode?.({
                // 返回值表示拦截否
                keys,
                data,
                callback(...arg) {
                    // 点击了不保存
                    newNodeKey.current &&
                        onCloseEditor(newNodeKey.current, '', 1);
                    onSelect(...arg);
                },
                treeData: treeMenuData,
            }))
        ) {
            return;
        }
        onSelect(keys, data);
    };

    return (
        <Spin spinning={loading} wrapperClassName="left-menu-wrap">
            <div className="left-tree-menu drawing-directory">
                <div className="header">
                    {menuTitle}
                    {showTitleAdd && (
                        <i
                            className="icon-plus outlined"
                            onClick={titleAddHandle}
                        />
                    )}
                </div>
                <Tree
                    ref={treeRef}
                    onSelect={(keys, data) => {
                        if (newNodeKey.current) {
                            setTimeout(onTreeClick.bind(null, keys, data));
                        } else {
                            onTreeClick(keys, data);
                        }
                    }}
                    multiple
                    onExpand={onExpand}
                    autoExpandParent
                    titleRender={titleRender}
                    selectedKeys={selectedKeys}
                    expandedKeys={expandedKeys}
                    treeData={treeData}
                />
            </div>
        </Spin>
    );
});
export default LeftTreeMenu;
